import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Search, MapPin, Calendar, ChevronLeft, ChevronRight, Heart, User } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ShokhSandeshPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [stateFilter, setStateFilter] = useState('all');
  const [states, setStates] = useState([]);

  const getPhotoUrl = (path) => path ? (path.startsWith('http') ? path : `${API}/api/files/${path}`) : null;

  useEffect(() => {
    axios.get(`${API}/api/states`).then(r => setStates(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ page, limit: 20 });
        if (search) params.append('search', search);
        if (stateFilter !== 'all') params.append('state', stateFilter);
        const res = await axios.get(`${API}/api/shokh-sandesh?${params}`);
        setItems(res.data.items);
        setTotal(res.data.total);
        setPages(res.data.pages);
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    fetchData();
  }, [page, search, stateFilter]);

  return (
    <div className="pb-24 md:pb-12" data-testid="shokh-sandesh-page">
      {/* Mobile Header */}
      <div className="md:hidden bg-gray-900 text-white p-6 pb-8 rounded-b-[2rem]">
        <div className="flex items-center gap-3 mb-4">
          <Heart className="w-7 h-7 text-white" fill="white" />
          <h1 className="font-outfit font-bold text-2xl">शोक संदेश</h1>
        </div>
        <p className="text-gray-300 text-sm">श्रद्धांजलि और शोक संदेश</p>
        <div className="mt-4 bg-white/10 rounded-xl p-1 flex items-center gap-2">
          <Search className="w-4 h-4 text-white/60 ml-2" />
          <input type="text" placeholder="नाम से खोजें..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            className="bg-transparent text-white placeholder-white/50 outline-none flex-1 text-sm py-2" data-testid="shokh-search" />
        </div>
      </div>

      {/* Desktop Header */}
      <div className="hidden md:block max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-3">
              <Heart className="w-8 h-8 text-gray-800" fill="currentColor" />
              <h1 className="font-outfit font-bold text-3xl text-gray-900">शोक संदेश</h1>
            </div>
            <p className="text-gray-500 mt-1">कुल {total} श्रद्धांजलि प्रकाशित</p>
          </div>
          <Link to="/shokh-sandesh/submit">
            <Button className="rounded-full bg-gray-900 hover:bg-gray-800 h-11 px-6">शोक संदेश दें</Button>
          </Link>
        </div>
        <div className="flex gap-3 mb-6">
          <div className="flex-1 relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input type="text" placeholder="नाम से खोजें..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-gray-900" data-testid="shokh-search-desktop" />
          </div>
          <select value={stateFilter} onChange={e => { setStateFilter(e.target.value); setPage(1); }}
            className="border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none bg-white" data-testid="shokh-state-filter">
            <option value="all">सभी राज्य</option>
            {states.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      {/* Cards Grid */}
      <div className="px-4 md:px-0 md:max-w-7xl md:mx-auto md:px-6 mt-4 md:mt-0">
        {loading ? (
          <div className="flex justify-center py-20"><div className="animate-spin w-8 h-8 border-4 border-gray-900 border-t-transparent rounded-full"></div></div>
        ) : items.length === 0 ? (
          <div className="text-center py-20 text-gray-500">
            <Heart className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p className="text-lg">कोई शोक संदेश नहीं मिला</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {items.map((item, idx) => (
              <motion.div key={item.sandesh_id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.05 }}>
                <Link to={`/shokh-sandesh/${item.sandesh_id}`} className="block" data-testid={`shokh-card-${item.sandesh_id}`}>
                  <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-lg transition-shadow">
                    <div className="relative aspect-[4/3] bg-gray-100">
                      {item.photo_url ? (
                        <img src={getPhotoUrl(item.photo_url)} alt={item.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center"><User className="w-16 h-16 text-gray-300" /></div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                      <div className="absolute bottom-3 left-3 right-3 text-white">
                        <h3 className="font-outfit font-bold text-lg leading-tight">{item.name}</h3>
                        {item.age && <span className="text-sm opacity-80">{item.age} वर्ष</span>}
                      </div>
                      <div className="absolute top-3 right-3">
                        <span className="px-2.5 py-1 bg-black/40 backdrop-blur-sm text-white text-xs rounded-full font-medium">श्रद्धांजलि</span>
                      </div>
                    </div>
                    <div className="p-3 space-y-1.5">
                      {item.date_of_death && (
                        <div className="flex items-center gap-1.5 text-xs text-gray-500">
                          <Calendar className="w-3.5 h-3.5" />
                          <span>निधन: {item.date_of_death}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-1.5 text-xs text-gray-500">
                        <MapPin className="w-3.5 h-3.5" />
                        <span>{item.village ? `${item.village}, ` : ''}{item.city}, {item.state}</span>
                      </div>
                      {item.message && <p className="text-xs text-gray-600 line-clamp-2 mt-1">{item.message}</p>}
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {pages > 1 && (
          <div className="flex justify-center items-center gap-4 mt-8 mb-4">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="rounded-full">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm text-gray-600">{page} / {pages}</span>
            <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(p => p + 1)} className="rounded-full">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>

      {/* Mobile Submit Button */}
      <div className="md:hidden fixed bottom-20 left-4 right-4 z-30">
        <Link to="/shokh-sandesh/submit">
          <Button className="w-full rounded-2xl h-12 bg-gray-900 hover:bg-gray-800 shadow-xl" data-testid="shokh-submit-mobile-btn">
            <Heart className="w-4 h-4 mr-2" />शोक संदेश दें
          </Button>
        </Link>
      </div>
    </div>
  );
}
